import {store} from '@/store';
import {userAtom} from '@/store/user';
import {buildMbApiUrl, fetchWithTimeout} from '@/util/request-utils';
import {fromPersistent} from '@/models/product-media';
import {type SearchParams, type SearchResponse} from '@/types/product-media';

export async function searchIO(params: SearchParams): Promise<SearchResponse> {
    const {...restParams} = params;

    try {
        const user = store.get(userAtom);
        if (!user?.auth_token) {
            return {search_after: null, total: 0, items: []};
        }

        // Build query params (excluding auth_token - that goes through buildMbApiUrl)
        const queryParams: Record<string, string | number> = {per_page: 48};
        for (const [key, value] of Object.entries(restParams)) {
            if (value === undefined) continue;
            // Skip filters object - not supported as query param by buildMbApiUrl
            if (key === 'filters') continue;
            // Convert arrays to comma-separated strings
            if (Array.isArray(value)) {
                queryParams[key] = value.join(',');
            } else if (typeof value === 'string' || typeof value === 'number') {
                queryParams[key] = value;
            }
        }

        const url = buildMbApiUrl(
            '/product_media/search.json',
            {auth_token: user.auth_token},
            queryParams
        );

        interface MediaSearchResponse {
            results?: unknown[];
            total?: number | {renders?: number};
            search_after?: string | null;
        }

        const fetchResponse = await fetchWithTimeout(url);
        if (!fetchResponse.ok) {
            return {search_after: null, total: 0, items: []};
        }
        const response: MediaSearchResponse = await fetchResponse.json();

        const items = Array.isArray(response?.results)
            ? response.results.map((media) =>
                  fromPersistent(media as Parameters<typeof fromPersistent>[0])
              )
            : [];

        // Handle cases where total is an object like {renders: 123} instead of just a number
        let total = 0;
        if (typeof response?.total === 'number') {
            total = response.total;
        } else if (
            typeof response?.total === 'object' &&
            response?.total?.renders !== undefined
        ) {
            total = response.total.renders;
        }

        return {
            search_after: response?.search_after ?? null,
            total,
            items: items,
        };
    } catch {
        return {search_after: null, total: 0, items: []};
    }
}
